'use client';

import { Box, HStack, VStack } from '@terra/ui-styled-system';
import { Button, Text } from '@terra/ui-design-system';
import { useCredentialEditStore } from '../../store/credentialEditStore';
import { useCredentialAuthMaterialEditStore } from '../../store/credentialAuthMaterialEditStore';
import { useCredentialUpdateQueueStore } from '../../store/credentialUpdateQueueStore';
import { CredentialCopilotPendingEditsSection } from './CredentialCopilotPendingEditsSection';
import { CredentialAuthMaterialCopilotPendingEditsSection } from './CredentialAuthMaterialCopilotPendingEditsSection';
import {
  mergeAcceptedCredentialPendingEditsById,
  type AcceptedCredentialPendingEdits,
} from './credentialPendingEditsAccept.utils';

type CredentialPendingEditBase = {
  id: string;
  username: string;
  role: string;
  roleId: string | null;
  roleDescription: string | null;
  headers: Array<{ key: string; value: string }>;
  cookies: Array<{ name: string; value: string }>;
};

type CredentialsCopilotPendingEditsPanelProps = {
  credentials: CredentialPendingEditBase[];
  onPendingEditsAccepted: (
    acceptedByCredentialId: Record<string, AcceptedCredentialPendingEdits>,
  ) => void;
};

export function CredentialsCopilotPendingEditsPanel({
  credentials,
  onPendingEditsAccepted,
}: CredentialsCopilotPendingEditsPanelProps) {
  const pendingAiEdits = useCredentialEditStore(
    (state) => state.pendingAiEdits,
  );
  const pendingAuthMaterialEdits = useCredentialAuthMaterialEditStore(
    (state) => state.pendingAiEdits,
  );
  const acceptAllPendingAiEdits = useCredentialEditStore(
    (state) => state.acceptAllPendingAiEdits,
  );
  const discardAllPendingAiEdits = useCredentialEditStore(
    (state) => state.discardAllPendingAiEdits,
  );
  const acceptAllPendingAuthMaterialEdits = useCredentialAuthMaterialEditStore(
    (state) => state.acceptAllPendingAiEdits,
  );
  const discardAllPendingAuthMaterialEdits = useCredentialAuthMaterialEditStore(
    (state) => state.discardAllPendingAiEdits,
  );

  const credentialsWithPending = credentials.filter(
    (credential) =>
      (pendingAiEdits[credential.id]?.fields.length ?? 0) > 0 ||
      (pendingAuthMaterialEdits[credential.id]?.fields.length ?? 0) > 0,
  );

  if (credentialsWithPending.length === 0) {
    return null;
  }

  const totalPendingFields = credentialsWithPending.reduce(
    (count, credential) => {
      return (
        count +
        (pendingAiEdits[credential.id]?.fields.length ?? 0) +
        (pendingAuthMaterialEdits[credential.id]?.fields.length ?? 0)
      );
    },
    0,
  );

  const baseFieldsByCredentialId = Object.fromEntries(
    credentialsWithPending.map((credential) => [
      credential.id,
      {
        role: credential.role,
        roleId: credential.roleId,
        roleDescription: credential.roleDescription,
      },
    ]),
  );

  const authMaterialBaseFieldsByCredentialId = Object.fromEntries(
    credentialsWithPending.map((credential) => [
      credential.id,
      {
        headers: credential.headers,
        cookies: credential.cookies,
      },
    ]),
  );

  const handleAcceptAll = () => {
    const acceptedRoleByCredentialId = acceptAllPendingAiEdits(
      baseFieldsByCredentialId,
    );
    const acceptedAuthMaterialByCredentialId =
      acceptAllPendingAuthMaterialEdits(authMaterialBaseFieldsByCredentialId);
    const acceptedByCredentialId = mergeAcceptedCredentialPendingEditsById({
      roleByCredentialId: acceptedRoleByCredentialId,
      authMaterialByCredentialId: acceptedAuthMaterialByCredentialId,
    });
    if (Object.keys(acceptedByCredentialId).length > 0) {
      onPendingEditsAccepted(acceptedByCredentialId);
    }
  };

  const handleDiscardAll = () => {
    discardAllPendingAiEdits();
    discardAllPendingAuthMaterialEdits();
    useCredentialUpdateQueueStore.getState().clearCredentialUpdateQueue();
  };

  return (
    <VStack alignItems="stretch" gap={4}>
      <Box
        p={4}
        borderRadius="lg"
        borderWidth="1px"
        borderColor="border.info"
        bg="bg.subtle"
      >
        <HStack justifyContent="space-between" alignItems="center" gap={2}>
          <VStack gap={1} alignItems="flex-start">
            <Text fontSize="sm" fontWeight="semibold">
              {totalPendingFields} AI-proposed credential change
              {totalPendingFields === 1 ? '' : 's'} across{' '}
              {credentialsWithPending.length} credential
              {credentialsWithPending.length === 1 ? '' : 's'}
            </Text>
            <Text fontSize="xs" color="fg.muted">
              Review below, or accept or discard everything at once.
            </Text>
          </VStack>
          <HStack gap={2}>
            <Button size="sm" variant="outline" onClick={handleDiscardAll}>
              Discard all
            </Button>
            <Button size="sm" variant="solid" onClick={handleAcceptAll}>
              Accept all
            </Button>
          </HStack>
        </HStack>
      </Box>
      {credentialsWithPending.map((credential) => (
        <VStack
          key={`copilot-pending-${credential.id}`}
          gap={3}
          alignItems="stretch"
        >
          <CredentialCopilotPendingEditsSection
            credentialId={credential.id}
            credentialLabel={credential.username}
            baseFields={{
              role: credential.role,
              roleId: credential.roleId,
              roleDescription: credential.roleDescription,
            }}
            onAccepted={(nextFields) => {
              onPendingEditsAccepted({
                [credential.id]: { role: nextFields },
              });
            }}
          />
          <CredentialAuthMaterialCopilotPendingEditsSection
            credentialId={credential.id}
            credentialLabel={credential.username}
            baseFields={{
              headers: credential.headers,
              cookies: credential.cookies,
            }}
            onAccepted={(nextFields) => {
              onPendingEditsAccepted({
                [credential.id]: { authMaterial: nextFields },
              });
            }}
          />
        </VStack>
      ))}
    </VStack>
  );
}
